import {
  Smartphone, Zap, RefreshCw, ShieldCheck,
  Droplets, Sparkles, CreditCard, Users, Truck, BarChart3,
} from "lucide-react";
import blackHero from "@/assets/products/black-hero.webp";
import blackFeatures from "@/assets/products/black-features.webp";
import blackTeam from "@/assets/products/black-team.webp";
import blackProfile from "@/assets/products/black-profile.webp";
import type { ProductPageDict } from "@/lib/shop/product-dict";

const SLUG = "black-pvc";
const SKU = "BC-PVCB";
const BASE_PRICE = 54;
const PRODUCT_URL_EN = "https://banana.cards/shop/black-pvc";
const PRODUCT_URL_VI = "https://banana.cards/vi/shop/black-pvc";
const RATING = { value: "4.81", count: "189" };

const BUNDLES = [
  { qty: 1,  label: "1 card",  perCard: 1.0 },
  { qty: 3,  label: "3 cards", perCard: 0.9,  save: 10, badge: "Save 10%" },
  { qty: 5,  label: "5 cards", perCard: 0.82, save: 18, badge: "Most popular" },
  { qty: 10, label: "10 cards", perCard: 0.72, save: 28, badge: "Best value" },
] as const;

const BUNDLES_VI = [
  { qty: 1,  label: "1 thẻ",  perCard: 1.0 },
  { qty: 3,  label: "3 thẻ",  perCard: 0.9,  save: 10, badge: "Tiết kiệm 10%" },
  { qty: 5,  label: "5 thẻ",  perCard: 0.82, save: 18, badge: "Phổ biến nhất" },
  { qty: 10, label: "10 thẻ", perCard: 0.72, save: 28, badge: "Giá trị nhất" },
] as const;

const GALLERY = [
  { src: blackHero,     alt: "Custom Black PVC AI business card front and back with NFC and QR" },
  { src: blackFeatures, alt: "Black PVC card features — foil logo, NFC chip, QR code" },
  { src: blackProfile,  alt: "Customizable digital profiles on iPhone" },
  { src: blackTeam,     alt: "Team & enterprise control dashboard" },
];

const GALLERY_VI = [
  { src: blackHero,     alt: "Thẻ PVC đen AI mặt trước và sau với NFC và mã QR" },
  { src: blackFeatures, alt: "Cấu tạo thẻ PVC đen — logo nhũ, chip NFC, mã QR" },
  { src: blackProfile,  alt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone" },
  { src: blackTeam,     alt: "Bảng điều khiển đội nhóm và doanh nghiệp" },
];

export const blackPvcDict: ProductPageDict = {
  slug: SLUG,
  routePath: "/shop/black-pvc",
  productUrl: PRODUCT_URL_EN,
  basePrice: BASE_PRICE,
  sku: SKU,
  rating: RATING,
  seo: {
    title: "Custom Black PVC AI Business Card — banana.cards",
    description: "Matte black PVC NFC business card with foil-printed logo, embedded NFC chip and QR code. AI-powered profile sharing and CRM built in.",
    ogTitle: "Custom Black PVC AI Business Card",
    ogDescription: "Premium matte black PVC NFC card. Tap once, share instantly, capture every contact — AI follow-ups built in.",
  },
  jsonLdDescription:
    "The Custom Black PVC AI Business Card combines a matte black PVC body with foil-printed branding, an embedded NFC chip and a printed QR code linked to your banana.cards profile.",
  eyebrow: "banana.cards · Black PVC",
  productName: "Custom Black PVC AI Business Card",
  productIntro:
    "Bold, confident, unmistakably premium. Matte black PVC with foil-printed branding, an embedded NFC chip and a printed QR code — a single card that handles introductions, capture and follow-up automatically.",
  ratingLabel: "4.81 · 189 reviews",
  perCardSuffix: "/ card · Free worldwide shipping over $50",
  saveBadge: (pct) => `Save ${pct}%`,
  bundles: BUNDLES,
  bundleSection: {
    eyebrow: "Bundle & save",
    heading: { pre: "Equip your team in", em: "matte black." },
    sub: "Each card carries its own profile — perfect for co-founders, sales teams or whole companies.",
    bundleLabel: "Bundle & save",
    perCardLabel: "per card",
    selected: "Selected",
    choose: "Choose",
  },
  usps: [
    { icon: Smartphone, label: "Share your details with one tap" },
    { icon: Zap,        label: "Collect their details back with one click" },
    { icon: RefreshCw,  label: "No app needed — works on both Apple & Android" },
    { icon: ShieldCheck,label: "Lifetime hardware guarantee" },
  ],
  features: {
    eyebrow: "Why black PVC",
    heading: { pre: "Confident black.", em: "Smart inside." },
    items: [
      { icon: CreditCard, title: "Foil-printed branding",      desc: "Gold, silver, rose-gold or matte white foil on rich matte black — premium and unmistakable in hand." },
      { icon: Sparkles,   title: "AI follow-ups built-in",     desc: "Every tap lands in your banana CRM, enriched and ready for AI-drafted follow-ups." },
      { icon: Droplets,   title: "Water & scratch resistant",  desc: "Premium PVC core handles spills, sticky fingers and daily wallet life without fading." },
      { icon: Users,      title: "Team-friendly rollout",      desc: "Bulk orders for whole teams — branded, fulfilled and billed in one go." },
      { icon: Truck,      title: "Free worldwide shipping",    desc: "Orders over $50 ship free to 100+ countries — 3-5 business days." },
      { icon: BarChart3,  title: "Real-time analytics",        desc: "See every tap, click and lead in your dashboard — know which events actually convert." },
    ],
  },
  anatomy: {
    eyebrow: "Card anatomy",
    heading: { pre: "Every detail,", em: "engineered." },
    image: blackFeatures,
    imageAlt: "Anatomy of the Black PVC AI business card — foil logo, NFC chip and QR code",
    items: [
      { t: "Foil-printed logo",        d: "Choose gold, silver, rose-gold or matte white foil — applied directly onto the matte black PVC for a stunning contrast." },
      { t: "Matte black PVC core",     d: "Soft-touch finish, water-resistant and scratch-resistant — built to live in wallets and pockets for years." },
      { t: "Embedded NFC chip",        d: "Tap to share your full profile to any modern iPhone or Android — no app required." },
      { t: "Printed QR code",          d: "A perfect fallback for older devices or thicker phone cases — every introduction lands." },
    ],
  },
  profile: {
    eyebrow: "Your profile, your way",
    heading: { pre: "One card.", em: "Infinite identities." },
    body: "Pick a layout, add your colors, photo, links, services and CTAs — and switch between work, side-hustle or recruiting profiles from the same PVC card.",
    bullets: [
      "12+ designer-made layouts & themes",
      "Add video, gallery, services, FAQs",
      "Multi-language profiles",
      "Lead-capture form on every card",
    ],
    image: blackProfile,
    imageAlt: "Customisable digital profiles on iPhone",
  },
  compare: {
    eyebrow: "Paper vs PVC",
    heading: { pre: "Paper cards get lost.", em: "Black PVC gets remembered." },
    sub: "You deserve something that actually works — and that people actually keep.",
    paperLabel: "Paper business cards",
    productLabel: "Custom Black PVC AI Business Card",
    paper: [
      "Hand out paper cards and hope they keep it",
      "Manually follow up days later",
      "Forget who you met or what you said",
      "No analytics, no CRM, no visibility",
      "You look like everyone else",
    ],
    product: [
      "Tap once, your details save instantly",
      "Automatic smart follow-ups",
      "Every contact captured automatically",
      "Real-time analytics show which events convert",
      "Bold, premium, unforgettable",
    ],
  },
  steps: {
    eyebrow: "How it works",
    heading: { pre: "You don't need to explain.", em: "You just tap." },
    sub: "They get everything that matters — instantly.",
    stepLabel: (n) => `Step ${n}`,
    items: [
      { title: "Tap once to share easily",   desc: "Tap your NFC Banana Card on any smartphone, no app required. Your profile, links and calendar open instantly." },
      { title: "They save you instantly",    desc: "They add you straight to their contacts — your info stays where they actually look: their phone." },
      { title: "You get their details back", desc: "Each new lead lands automatically in your Banana dashboard and syncs to your CRM — no manual entry." },
    ],
  },
  team: {
    eyebrow: "For teams",
    heading: { pre: "Built for", em: "teams that close." },
    body: "Roll out branded matte black cards across your whole team, manage every profile from one dashboard, and route every new contact straight into your shared CRM.",
    cta: "Order for your team",
    image: blackTeam,
    imageAlt: "Team and enterprise control dashboard",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "FAQ",
    heading: { pre: "Frequently asked", em: "questions." },
    items: [
      { q: "What makes the Black PVC card stand out?", a: "The combination of matte black soft-touch finish with foil-printed branding makes every card feel premium in hand — and the embedded NFC + AI follow-ups make it actually useful, not just pretty." },
      { q: "What foil colors can I choose?", a: "Gold, silver, rose-gold, copper and matte white foil. Each contrasts beautifully against the matte black PVC base." },
      { q: "How do I customize my black PVC card?", a: "After checkout we send you a short design brief. Upload your logo, pick your foil color, choose front/back artwork — our team mocks it up, you approve, we produce." },
      { q: "Does the card work through phone cases?", a: "Yes. The embedded NFC antenna reads through most silicone, leather and plastic cases. The printed QR is a perfect fallback." },
      { q: "What digital features come with the card?", a: "Every card includes a banana.cards profile: unlimited links, multi-language profiles, lead capture form, Apple & Google Wallet pass, real-time analytics and CRM sync." },
      { q: "Is the foil scratch resistant?", a: "Yes. The foil is heat-stamped and sealed under a protective layer — it stays sharp through daily wallet life." },
      { q: "Can I use the card with my existing CRM?", a: "Yes. Banana syncs natively with HubSpot, Salesforce, Pipedrive, Notion, Google Contacts and 50+ tools via Zapier." },
      { q: "Do I need batteries or to charge the card?", a: "No. The NFC chip is passive — nothing to charge, ever." },
      { q: "Can I track how many times my card is used?", a: "Yes. Your dashboard shows every tap and QR scan in real time." },
      { q: "How long does production take?", a: "Most cards ship within 5–7 business days of design approval." },
    ],
  },
  outro: {
    heading: { pre: "Network with", em: "premium on your side." },
    body: "Order before 3pm and we ship the next business day. Free worldwide shipping over $50.",
    primaryCta: "Get my Black PVC card",
    secondaryCta: "See all products",
    shopHref: "/shop",
  },
  buyBox: { addToCart: "Add to cart" },
  gallery: GALLERY,
};

export const blackPvcDictVi: ProductPageDict = {
  ...blackPvcDict,
  routePath: "/vi/shop/black-pvc",
  productUrl: PRODUCT_URL_VI,
  seo: {
    title: "Thẻ Danh Thiếp PVC Đen AI Tùy Chỉnh — banana.cards",
    description: "Thẻ NFC PVC đen mờ in nhũ, chip NFC tích hợp và mã QR. Chia sẻ hồ sơ bằng AI và CRM tích hợp sẵn.",
    ogTitle: "Thẻ Danh Thiếp PVC Đen AI Tùy Chỉnh",
    ogDescription: "Thẻ NFC PVC đen mờ cao cấp. Chạm một lần, chia sẻ tức thì, thu thập mọi liên hệ — tích hợp AI theo dõi tự động.",
    locale: "vi_VN",
  },
  jsonLdDescription:
    "Thẻ Danh Thiếp PVC Đen AI Tùy Chỉnh kết hợp thân PVC đen mờ với thương hiệu in nhũ, chip NFC tích hợp và mã QR liên kết với hồ sơ banana.cards.",
  eyebrow: "banana.cards · PVC đen",
  productName: "Thẻ Danh Thiếp PVC Đen AI Tùy Chỉnh",
  productIntro:
    "Mạnh mẽ, tự tin, đẳng cấp không thể nhầm lẫn. PVC đen mờ với thương hiệu in nhũ, chip NFC tích hợp và mã QR in — một thẻ xử lý giới thiệu, thu thập và theo dõi tự động.",
  ratingLabel: "4,81 · 189 đánh giá",
  perCardSuffix: "/ thẻ · Miễn phí vận chuyển toàn cầu cho đơn trên $50",
  saveBadge: (pct) => `Tiết kiệm ${pct}%`,
  bundles: BUNDLES_VI,
  bundleSection: {
    eyebrow: "Mua combo, tiết kiệm",
    heading: { pre: "Trang bị cho đội bằng", em: "đen mờ." },
    sub: "Mỗi thẻ có hồ sơ riêng — hoàn hảo cho đồng sáng lập, đội bán hàng hoặc cả công ty.",
    bundleLabel: "Mua combo, tiết kiệm",
    perCardLabel: "mỗi thẻ",
    selected: "Đã chọn",
    choose: "Chọn",
  },
  usps: [
    { icon: Smartphone, label: "Chia sẻ thông tin chỉ với một cú chạm" },
    { icon: Zap,        label: "Thu lại thông tin của họ chỉ với một cú nhấp" },
    { icon: RefreshCw,  label: "Không cần ứng dụng — hoạt động trên cả iPhone và Android" },
    { icon: ShieldCheck,label: "Bảo hành phần cứng trọn đời" },
  ],
  features: {
    eyebrow: "Vì sao chọn PVC đen",
    heading: { pre: "Đen tự tin.", em: "Thông minh bên trong." },
    items: [
      { icon: CreditCard, title: "Thương hiệu in nhũ",         desc: "Nhũ vàng, bạc, vàng hồng hoặc trắng mờ trên nền đen mờ — cao cấp và không thể nhầm lẫn." },
      { icon: Sparkles,   title: "AI theo dõi tích hợp",       desc: "Mỗi cú chạm đều được lưu vào CRM banana, làm giàu dữ liệu và sẵn sàng cho AI soạn lời theo dõi." },
      { icon: Droplets,   title: "Chống nước & chống xước",   desc: "Lõi PVC cao cấp chịu được nước và sử dụng hàng ngày." },
      { icon: Users,      title: "Triển khai dễ cho đội",     desc: "Đặt số lượng lớn cho cả đội — gắn thương hiệu, giao hàng và thanh toán cùng một lần." },
      { icon: Truck,      title: "Vận chuyển toàn cầu miễn phí", desc: "Đơn trên $50 miễn phí giao đến hơn 100 quốc gia." },
      { icon: BarChart3,  title: "Phân tích thời gian thực",   desc: "Xem mọi cú chạm, lượt nhấp và khách tiềm năng trên bảng điều khiển." },
    ],
  },
  anatomy: {
    eyebrow: "Cấu tạo thẻ",
    heading: { pre: "Mọi chi tiết,", em: "đều được tinh chỉnh." },
    image: blackFeatures,
    imageAlt: "Cấu tạo Thẻ PVC đen AI — logo nhũ, chip NFC và mã QR",
    items: [
      { t: "Logo in nhũ",            d: "Chọn nhũ vàng, bạc, vàng hồng hoặc trắng mờ — áp lên PVC đen mờ tạo tương phản ấn tượng." },
      { t: "Lõi PVC đen mờ",         d: "Hoàn thiện cảm giác mềm, chống nước và chống xước — bền nhiều năm trong ví và túi." },
      { t: "Chip NFC tích hợp",      d: "Chạm để chia sẻ hồ sơ với mọi iPhone hoặc Android hiện đại — không cần ứng dụng." },
      { t: "Mã QR in sẵn",           d: "Phương án dự phòng hoàn hảo cho thiết bị cũ hoặc ốp lưng dày." },
    ],
  },
  profile: {
    eyebrow: "Hồ sơ của bạn, theo cách của bạn",
    heading: { pre: "Một thẻ.", em: "Vô vàn bản dạng." },
    body: "Chọn bố cục, thêm màu sắc, ảnh, liên kết, dịch vụ và CTA — và chuyển đổi giữa hồ sơ công việc, dự án phụ hoặc tuyển dụng từ cùng một thẻ PVC.",
    bullets: [
      "12+ bố cục & chủ đề thiết kế sẵn",
      "Thêm video, thư viện, dịch vụ, FAQ",
      "Hồ sơ đa ngôn ngữ",
      "Biểu mẫu thu khách tiềm năng trên mọi thẻ",
    ],
    image: blackProfile,
    imageAlt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone",
  },
  compare: {
    eyebrow: "Giấy vs PVC",
    heading: { pre: "Thẻ giấy bị thất lạc.", em: "PVC đen được ghi nhớ." },
    sub: "Bạn xứng đáng có thứ thực sự hoạt động — và người ta thực sự giữ lại.",
    paperLabel: "Thẻ danh thiếp giấy",
    productLabel: "Thẻ Danh Thiếp PVC Đen AI Tùy Chỉnh",
    paper: [
      "Phát thẻ giấy và hy vọng họ giữ",
      "Theo dõi thủ công vài ngày sau",
      "Quên mất bạn đã gặp ai và nói gì",
      "Không phân tích, không CRM, không tầm nhìn",
      "Bạn trông giống mọi người khác",
    ],
    product: [
      "Chạm một lần, thông tin được lưu tức thì",
      "Theo dõi thông minh tự động",
      "Mọi liên hệ được thu thập tự động",
      "Phân tích thời gian thực cho biết sự kiện nào chuyển đổi",
      "Mạnh mẽ, cao cấp, khó quên",
    ],
  },
  steps: {
    eyebrow: "Cách hoạt động",
    heading: { pre: "Bạn không cần giải thích.", em: "Bạn chỉ cần chạm." },
    sub: "Họ nhận được mọi thứ quan trọng — ngay lập tức.",
    stepLabel: (n) => `Bước ${n}`,
    items: [
      { title: "Chạm một lần để chia sẻ",  desc: "Chạm thẻ Banana NFC vào bất kỳ điện thoại nào, không cần ứng dụng." },
      { title: "Họ lưu bạn tức thì",       desc: "Họ thêm bạn vào danh bạ — thông tin nằm đúng nơi họ thực sự nhìn." },
      { title: "Bạn nhận lại thông tin",   desc: "Mỗi khách tiềm năng tự động vào bảng điều khiển Banana và đồng bộ CRM." },
    ],
  },
  team: {
    eyebrow: "Dành cho đội nhóm",
    heading: { pre: "Xây cho", em: "đội bán hàng chốt sale." },
    body: "Triển khai thẻ đen mờ gắn thương hiệu cho cả đội, quản lý mọi hồ sơ trên một bảng điều khiển.",
    cta: "Đặt cho đội của bạn",
    image: blackTeam,
    imageAlt: "Bảng điều khiển đội và doanh nghiệp",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "Câu hỏi thường gặp",
    heading: { pre: "Câu hỏi", em: "thường gặp." },
    items: [
      { q: "Điều gì làm Thẻ PVC Đen nổi bật?", a: "Sự kết hợp giữa lớp hoàn thiện đen mờ cảm giác mềm với thương hiệu in nhũ khiến mỗi thẻ cảm thấy cao cấp — và NFC tích hợp + AI theo dõi khiến nó thực sự hữu ích." },
      { q: "Có thể chọn màu nhũ nào?", a: "Nhũ vàng, bạc, vàng hồng, đồng và trắng mờ. Mỗi loại tương phản đẹp với nền PVC đen mờ." },
      { q: "Làm sao tùy chỉnh thẻ PVC đen?", a: "Sau khi đặt hàng, chúng tôi gửi bản brief thiết kế. Tải logo, chọn màu nhũ, mặt trước/sau — đội thiết kế làm mockup, bạn duyệt, chúng tôi sản xuất." },
      { q: "Thẻ có hoạt động qua ốp điện thoại không?", a: "Có. Ăng-ten NFC đọc qua hầu hết ốp silicone, da và nhựa. Mã QR in là phương án dự phòng." },
      { q: "Thẻ có tính năng số gì?", a: "Mỗi thẻ bao gồm hồ sơ banana.cards: liên kết không giới hạn, hồ sơ đa ngôn ngữ, biểu mẫu thu khách, thẻ Apple & Google Wallet, phân tích thời gian thực và đồng bộ CRM." },
      { q: "Nhũ có chống xước không?", a: "Có. Nhũ được dập nhiệt và niêm phong dưới lớp bảo vệ — giữ sắc nét qua sử dụng hàng ngày." },
      { q: "Có thể dùng với CRM hiện tại không?", a: "Có. Banana đồng bộ với HubSpot, Salesforce, Pipedrive, Notion, Google Contacts và 50+ công cụ qua Zapier." },
      { q: "Có cần pin hoặc sạc thẻ không?", a: "Không. Chip NFC là thụ động — không pin, không sạc." },
      { q: "Có thể theo dõi thẻ được dùng bao nhiêu lần?", a: "Có. Bảng điều khiển hiển thị mọi cú chạm và quét QR theo thời gian thực." },
      { q: "Sản xuất mất bao lâu?", a: "Hầu hết thẻ được giao trong 5-7 ngày làm việc kể từ khi duyệt thiết kế." },
    ],
  },
  outro: {
    heading: { pre: "Kết nối với", em: "đẳng cấp về phía bạn." },
    body: "Đặt trước 3 giờ chiều, chúng tôi giao vào ngày làm việc kế tiếp. Miễn phí vận chuyển toàn cầu cho đơn trên $50.",
    primaryCta: "Mua thẻ PVC đen",
    secondaryCta: "Xem tất cả sản phẩm",
    shopHref: "/shop",
  },
  buyBox: { addToCart: "Thêm vào giỏ" },
  gallery: GALLERY_VI,
};

const PRODUCT_URL_EL = "https://banana.cards/el/shop/black-pvc";
const BUNDLES_EL = [
  {
    qty: 1,
    label: "1 κάρτα",
    perCard: 1.0
  },
  {
    qty: 3,
    label: "3 κάρτες",
    perCard: 0.9,
    save: 10,
    badge: "Κέρδος 10%"
  },
  {
    qty: 5,
    label: "5 κάρτες",
    perCard: 0.82,
    save: 18,
    badge: "Δημοφιλέστερο"
  },
  {
    qty: 10,
    label: "10 κάρτες",
    perCard: 0.72,
    save: 28,
    badge: "Καλύτερη τιμή"
  }
] as const;
const GALLERY_EL = [
  {
    src: blackHero,
    alt: "Μπροστινή και πίσω όψη εξατομικευμένης μαύρης επαγγελματικής κάρτας PVC με AI, NFC και QR"
  },
  {
    src: blackFeatures,
    alt: "Μαύρη κάρτα PVC — λογότυπο foil, τσιπ NFC και κωδικός QR"
  },
  {
    src: blackProfile,
    alt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone"
  },
  {
    src: blackTeam,
    alt: "Πίνακας ελέγχου για ομάδες και επιχειρήσεις"
  }
];
export const blackPvcDictEl: ProductPageDict = {
  ...blackPvcDict,
  routePath: "/el/shop/black-pvc",
  productUrl: PRODUCT_URL_EL,
  seo: {
    title: "Μαύρη κάρτα PVC με AI — banana.cards",
    description: "Μαύρη ματ κάρτα NFC από PVC με λογότυπο foil, τσιπ NFC και QR. Άμεση κοινοποίηση προφίλ με AI και ενσωματωμένο CRM.",
    ogTitle: "Εξατομικευμένη μαύρη επαγγελματική κάρτα PVC με AI",
    ogDescription: "Μαύρη ματ κάρτα NFC από PVC. Ένα άγγιγμα, άμεση κοινοποίηση, καταγραφή κάθε επαφής — με επανεπικοινωνία μέσω AI.",
    locale: "el_GR"
  },
  jsonLdDescription: "Η εξατομικευμένη μαύρη επαγγελματική κάρτα PVC με AI συνδυάζει μαύρο ματ σώμα PVC με εκτύπωση της εταιρικής σας ταυτότητας σε foil, ενσωματωμένο τσιπ NFC και τυπωμένο κωδικό QR που οδηγεί στο προφίλ σας στο banana.cards.",
  eyebrow: "banana.cards · Μαύρο PVC",
  productName: "Εξατομικευμένη μαύρη επαγγελματική κάρτα PVC με AI",
  productIntro: "Δυναμική, με αυτοπεποίθηση και αδιαμφισβήτητη ποιότητα. Μαύρο ματ PVC με εκτύπωση της εταιρικής σας ταυτότητας σε foil, ενσωματωμένο τσιπ NFC και τυπωμένο κωδικό QR — μία κάρτα που αναλαμβάνει αυτόματα τη γνωριμία, την καταγραφή επαφών και την επανεπικοινωνία.",
  ratingLabel: "4,81 · 189 αξιολογήσεις",
  perCardSuffix: "/ κάρτα · Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50",
  saveBadge: (pct) => `Κέρδος ${pct}%`,
  bundles: BUNDLES_EL,
  bundleSection: {
    eyebrow: "Περισσότερες κάρτες, μεγαλύτερο όφελος",
    heading: {
      pre: "Εξοπλίστε την ομάδα σας με",
      em: "μαύρο ματ στυλ."
    },
    sub: "Κάθε κάρτα έχει το δικό της προφίλ — ιδανική για συνιδρυτές, ομάδες πωλήσεων ή ολόκληρες εταιρείες.",
    bundleLabel: "Πακέτα με όφελος",
    perCardLabel: "ανά κάρτα",
    selected: "Επιλεγμένο",
    choose: "Επιλογή"
  },
  usps: [
    {
      icon: Smartphone,
      label: "Μοιραστείτε τα στοιχεία σας με ένα άγγιγμα"
    },
    {
      icon: Zap,
      label: "Λάβετε και τα δικά τους στοιχεία με ένα κλικ"
    },
    {
      icon: RefreshCw,
      label: "Χωρίς εφαρμογή — λειτουργεί σε Apple και Android"
    },
    {
      icon: ShieldCheck,
      label: "Εγγύηση εφ’ όρου ζωής για την κάρτα"
    }
  ],
  features: {
    eyebrow: "Γιατί μαύρο PVC",
    heading: {
      pre: "Μαύρο με χαρακτήρα.",
      em: "Έξυπνο στην πράξη."
    },
    items: [
      {
        icon: CreditCard,
        title: "Το λογότυπό σας με εκτύπωση foil",
        desc: "Χρυσό, ασημί, ροζ χρυσό ή λευκό ματ foil σε βαθύ μαύρο ματ — κορυφαία αισθητική και μοναδική αίσθηση στο χέρι."
      },
      {
        icon: Sparkles,
        title: "Ενσωματωμένη επανεπικοινωνία με AI",
        desc: "Κάθε άγγιγμα καταγράφεται στο Banana CRM, με εμπλουτισμένα στοιχεία, έτοιμα για μηνύματα επανεπικοινωνίας που συντάσσει το AI."
      },
      {
        icon: Droplets,
        title: "Αντοχή σε νερό και γρατσουνιές",
        desc: "Το υψηλής ποιότητας PVC αντέχει σε υγρά, δαχτυλιές και καθημερινή χρήση στο πορτοφόλι, χωρίς να ξεθωριάζει."
      },
      {
        icon: Users,
        title: "Εύκολη διάθεση σε όλη την ομάδα",
        desc: "Μαζικές παραγγελίες για όλη την ομάδα — με την εταιρική σας ταυτότητα, ενιαία αποστολή και τιμολόγηση."
      },
      {
        icon: Truck,
        title: "Δωρεάν αποστολή παγκοσμίως",
        desc: "Παραγγελίες άνω των $50 αποστέλλονται δωρεάν σε περισσότερες από 100 χώρες — σε 3–5 εργάσιμες ημέρες."
      },
      {
        icon: BarChart3,
        title: "Στατιστικά σε πραγματικό χρόνο",
        desc: "Δείτε κάθε άγγιγμα, κλικ και υποψήφιο πελάτη στον πίνακα ελέγχου — και μάθετε ποιες εκδηλώσεις φέρνουν πραγματικά αποτελέσματα."
      }
    ]
  },
  anatomy: {
    eyebrow: "Η κατασκευή της κάρτας",
    heading: {
      pre: "Κάθε λεπτομέρεια,",
      em: "μελετημένη."
    },
    image: blackFeatures,
    imageAlt: "Κατασκευή της μαύρης επαγγελματικής κάρτας PVC με AI — λογότυπο foil, τσιπ NFC και κωδικός QR",
    items: [
      {
        t: "Λογότυπο με εκτύπωση foil",
        d: "Επιλέξτε χρυσό, ασημί, ροζ χρυσό ή λευκό ματ foil — εφαρμόζεται απευθείας στο μαύρο ματ PVC για εντυπωσιακή αντίθεση."
      },
      {
        t: "Μαύρος ματ πυρήνας PVC",
        d: "Απαλή υφή, αντοχή στο νερό και τις γρατσουνιές — σχεδιασμένη για να αντέχει για χρόνια σε πορτοφόλια και τσέπες."
      },
      {
        t: "Ενσωματωμένο τσιπ NFC",
        d: "Αγγίζετε την κάρτα σε οποιοδήποτε σύγχρονο iPhone ή Android και μοιράζεστε ολόκληρο το προφίλ σας — χωρίς εφαρμογή."
      },
      {
        t: "Τυπωμένος κωδικός QR",
        d: "Η ιδανική εναλλακτική για παλαιότερες συσκευές ή πιο χοντρές θήκες κινητών — για να μη χάνεται καμία γνωριμία."
      }
    ]
  },
  profile: {
    eyebrow: "Το προφίλ σας, όπως το θέλετε",
    heading: {
      pre: "Μία κάρτα.",
      em: "Απεριόριστες ταυτότητες."
    },
    body: "Επιλέξτε διάταξη, προσθέστε τα χρώματα, τη φωτογραφία, τους συνδέσμους, τις υπηρεσίες και τα κουμπιά δράσης σας — και αλλάξτε ανάμεσα σε προφίλ για την εργασία, την παράλληλη δραστηριότητα ή τις προσλήψεις σας, με την ίδια κάρτα PVC.",
    bullets: [
      "Πάνω από 12 επαγγελματικά σχεδιασμένες διατάξεις και θέματα",
      "Προσθέστε βίντεο, συλλογή εικόνων, υπηρεσίες και συχνές ερωτήσεις",
      "Πολύγλωσσα προφίλ",
      "Φόρμα συλλογής στοιχείων υποψήφιων πελατών σε κάθε κάρτα"
    ],
    image: blackProfile,
    imageAlt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone"
  },
  compare: {
    eyebrow: "Χαρτί ή PVC",
    heading: {
      pre: "Οι χάρτινες κάρτες χάνονται.",
      em: "Το μαύρο PVC μένει αξέχαστο."
    },
    sub: "Αξίζετε κάτι που λειτουργεί πραγματικά — και που οι άλλοι θέλουν να κρατήσουν.",
    paperLabel: "Χάρτινες επαγγελματικές κάρτες",
    productLabel: "Εξατομικευμένη μαύρη επαγγελματική κάρτα PVC με AI",
    paper: [
      "Μοιράζετε χάρτινες κάρτες και ελπίζετε να τις κρατήσουν",
      "Επικοινωνείτε ξανά μόνοι σας, μέρες αργότερα",
      "Ξεχνάτε ποιον συναντήσατε ή τι συζητήσατε",
      "Χωρίς στατιστικά, CRM ή εικόνα των αποτελεσμάτων",
      "Δεν ξεχωρίζετε από τους υπόλοιπους"
    ],
    product: [
      "Ένα άγγιγμα, άμεση αποθήκευση των στοιχείων σας",
      "Αυτόματη, έξυπνη επανεπικοινωνία",
      "Αυτόματη καταγραφή κάθε επαφής",
      "Στατιστικά σε πραγματικό χρόνο για τις εκδηλώσεις που φέρνουν αποτελέσματα",
      "Δυναμική παρουσία, κορυφαία ποιότητα, αξέχαστη εντύπωση"
    ]
  },
  steps: {
    eyebrow: "Πώς λειτουργεί",
    heading: {
      pre: "Δεν χρειάζονται εξηγήσεις.",
      em: "Μόνο ένα άγγιγμα."
    },
    sub: "Όλα όσα χρειάζονται, στη διάθεσή τους — αμέσως.",
    stepLabel: (n) => `Βήμα ${n}`,
    items: [
      {
        title: "Μοιραστείτε εύκολα με ένα άγγιγμα",
        desc: "Αγγίζετε την κάρτα NFC της Banana σε οποιοδήποτε smartphone, χωρίς να χρειάζεται εφαρμογή. Το προφίλ, οι σύνδεσμοι και το ημερολόγιό σας ανοίγουν αμέσως."
      },
      {
        title: "Σας αποθηκεύουν αμέσως",
        desc: "Σας προσθέτουν απευθείας στις επαφές τους — τα στοιχεία σας μένουν εκεί που πραγματικά τα αναζητούν: στο κινητό τους."
      },
      {
        title: "Λαμβάνετε και τα δικά τους στοιχεία",
        desc: "Κάθε νέος υποψήφιος πελάτης καταγράφεται αυτόματα στον πίνακα ελέγχου Banana και συγχρονίζεται με το CRM σας — χωρίς χειροκίνητη καταχώριση."
      }
    ]
  },
  team: {
    eyebrow: "Για ομάδες",
    heading: {
      pre: "Σχεδιασμένη για",
      em: "ομάδες που κλείνουν συμφωνίες."
    },
    body: "Εξοπλίστε όλη την ομάδα σας με μαύρες ματ κάρτες με την εταιρική σας ταυτότητα, διαχειριστείτε κάθε προφίλ από έναν πίνακα ελέγχου και στείλτε κάθε νέα επαφή απευθείας στο κοινό σας CRM.",
    cta: "Παραγγείλετε για την ομάδα σας",
    image: blackTeam,
    imageAlt: "Πίνακας ελέγχου για ομάδες και επιχειρήσεις",
    teamsHref: "/teams"
  },
  faq: {
    eyebrow: "Συχνές ερωτήσεις",
    heading: {
      pre: "Συχνές",
      em: "ερωτήσεις."
    },
    items: [
      {
        q: "Τι κάνει τη μαύρη κάρτα PVC να ξεχωρίζει;",
        a: "Η μαύρη ματ επιφάνεια με απαλή υφή και η εκτύπωση foil χαρίζουν αίσθηση υψηλής ποιότητας — ενώ το ενσωματωμένο NFC και η επανεπικοινωνία με AI την κάνουν πραγματικά χρήσιμη, όχι απλώς όμορφη."
      },
      {
        q: "Ποια χρώματα foil μπορώ να επιλέξω;",
        a: "Χρυσό, ασημί, ροζ χρυσό, χάλκινο και λευκό ματ. Όλα δημιουργούν υπέροχη αντίθεση με τη μαύρη ματ βάση PVC."
      },
      {
        q: "Πώς εξατομικεύω τη μαύρη κάρτα PVC μου;",
        a: "Μετά την ολοκλήρωση της παραγγελίας, σας στέλνουμε ένα σύντομο έντυπο με τις σχεδιαστικές σας προτιμήσεις. Ανεβάζετε το λογότυπό σας, επιλέγετε χρώμα foil και σχέδιο για τις δύο όψεις — η ομάδα μας ετοιμάζει την προεπισκόπηση, εσείς εγκρίνετε και εμείς προχωράμε στην παραγωγή."
      },
      {
        q: "Λειτουργεί η κάρτα μέσα από θήκες κινητών;",
        a: "Ναι. Η ενσωματωμένη κεραία NFC λειτουργεί μέσα από τις περισσότερες θήκες σιλικόνης, δέρματος και πλαστικού. Ο τυπωμένος κωδικός QR είναι η ιδανική εναλλακτική."
      },
      {
        q: "Ποιες ψηφιακές δυνατότητες περιλαμβάνει η κάρτα;",
        a: "Κάθε κάρτα περιλαμβάνει προφίλ banana.cards: απεριόριστους συνδέσμους, πολύγλωσσα προφίλ, φόρμα συλλογής στοιχείων υποψήφιων πελατών, ψηφιακή κάρτα για Apple Wallet και Google Wallet, στατιστικά σε πραγματικό χρόνο και συγχρονισμό με CRM."
      },
      {
        q: "Είναι η εκτύπωση foil ανθεκτική στις γρατσουνιές;",
        a: "Ναι. Το foil εφαρμόζεται με θερμοτυπία και σφραγίζεται κάτω από προστατευτική στρώση — διατηρεί την ευκρίνειά του στην καθημερινή χρήση στο πορτοφόλι."
      },
      {
        q: "Μπορώ να χρησιμοποιήσω την κάρτα με το υπάρχον CRM μου;",
        a: "Ναι. Η Banana συγχρονίζεται απευθείας με HubSpot, Salesforce, Pipedrive, Notion και Google Contacts, καθώς και με περισσότερα από 50 εργαλεία μέσω Zapier."
      },
      {
        q: "Χρειάζεται η κάρτα μπαταρίες ή φόρτιση;",
        a: "Όχι. Το τσιπ NFC είναι παθητικό — δεν χρειάζεται ποτέ φόρτιση ή μπαταρίες."
      },
      {
        q: "Μπορώ να βλέπω πόσες φορές χρησιμοποιείται η κάρτα μου;",
        a: "Ναι. Ο πίνακας ελέγχου εμφανίζει κάθε άγγιγμα και σάρωση QR σε πραγματικό χρόνο."
      },
      {
        q: "Πόσο διαρκεί η παραγωγή;",
        a: "Οι περισσότερες κάρτες αποστέλλονται εντός 5–7 εργάσιμων ημερών από την έγκριση του σχεδίου."
      }
    ]
  },
  outro: {
    heading: {
      pre: "Δικτυωθείτε με",
      em: "το κύρος στο πλευρό σας."
    },
    body: "Παραγγείλετε πριν τις 15:00 και αποστέλλουμε την επόμενη εργάσιμη ημέρα. Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50.",
    primaryCta: "Αποκτήστε τη μαύρη κάρτα PVC σας",
    secondaryCta: "Δείτε όλα τα προϊόντα",
    shopHref: "/shop"
  },
  buyBox: {
    addToCart: "Προσθήκη στο καλάθι"
  },
  gallery: GALLERY_EL
};
